import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  Button,
  IconButton,
  Box,
  FormHelperText,
  Divider,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { toastError, toastSuccess } from "src/utils/utils";

const schema = yup.object().shape({
  name: yup.string().required("Region name is required"),
});

const DialogAddRegion = ({ open, onClose, onSuccess, mode, dataToEdit }) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: dataToEdit?.name || "",
    },
    resolver: yupResolver(schema),
  });

  // Reset form when dataToEdit changes
  React.useEffect(() => {
    if (dataToEdit && mode === "edit") {
      reset({
        name: dataToEdit.name || "",
      });
    } else {
      reset({
        name: "",
      });
    }
  }, [dataToEdit, mode, reset]);

  const [loading, setLoading] = useState(false);

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#fff",
      height: "48px",
      "& fieldset": {
        border: "1px solid #B3C4CC",
      },
      "&:hover fieldset": {
        borderColor: "#D1D5DB",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#17D96C",
        borderWidth: "2px",
      },
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "14px",
      color: "#000",
      "&::placeholder": {
        color: "#A3B1C0",
        fontWeight: 500,
        opacity: 1,
      },
    },
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data) => {
    setLoading(true);
    
    let payload = {};
    
    if (mode === "edit") {
      payload = {
        name: data.name,
        is_active: dataToEdit?.is_active || true,
      };
    } else {
      payload = {
        name: data.name,
      };
    }

    let apiInstance = null;
    if (mode === "edit") {
      apiInstance = axiosInstance.patch(
        ApiEndPoints.REGIONS.update(dataToEdit.id),
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    } else {
      apiInstance = axiosInstance.post(ApiEndPoints.REGIONS.create, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    }

    apiInstance
      .then((response) => response.data)
      .then((response) => {
        onSuccess();
        toastSuccess(response.message);
        onClose();
      })
      .catch((error) => {
        toastError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="fm-h6"
            sx={{ fontWeight: 800, color: "#000000" }}
          >
            {mode === "edit" ? "Edit Region" : "Add New Region"}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <HighlightOffIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Region Name */}
          <FormControl fullWidth sx={{ mb: 5 }}>
            <FormLabel
              htmlFor="name"
              sx={{
                fontSize: "12px",
                color: "#6C7278",
                fontWeight: 500,
                mb: 1,
              }}
            >
              Region Name
            </FormLabel>
            <Controller
              name="name"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TextField
                  onChange={onChange}
                  id="name"
                  value={value}
                  placeholder="Enter Region Name"
                  sx={textFieldStyles}
                  variant="outlined"
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              )}
            />
            {errors.name && (
              <FormHelperText sx={{ color: "error.main" }}>
                {errors.name.message}
              </FormHelperText>
            )}
          </FormControl>
        </form>
      </DialogContent>
      
      <DialogActions sx={{ gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            color: "#000000",
            borderColor: "#CFCFCF",
            fontSize: "16px",
            fontWeight: 600,
            padding: "12px 24px",
            borderRadius: "8px",
            width: "100%",
            height: "48px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#F9FAFB",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: "#17D96C",
            color: "#FFFFFF",
            fontSize: "16px",
            fontWeight: 600,
            padding: "20px 24px",
            width: "100%",
            borderRadius: "12px",
            height: "48px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#17D96C",
            },
            "&:disabled": {
              backgroundColor: "#B3C4CC",
            },
          }}
        >
          {loading ? "Processing..." : mode === "edit" ? "Update Region" : "Add Region"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogAddRegion;
